import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Sparkles, TrendingUp, Award, ChevronDown } from "lucide-react";
import { HeroShowcase } from "../components/HeroShowcase";
import { Reveal } from "../components/Reveal";
import { TestimonialsSection } from "../components/TestimonialsSection";
import { Seo } from "../components/Seo";
import { SERVICES } from "../lib/services";
import { PAGE_SEO, FAQS, organizationJsonLd, faqJsonLd } from "../lib/seo";
import { Toaster } from "sonner";
import { useState } from "react";

const trustLine = [
  "Business Registration", "ROC & Secretarial", "FSSAI Licensing",
  "Labour Compliance", "Fire & Environmental", "Trademark & IP",
  "M&A Due Diligence", "Import/Export",
  "Annual Compliance", "Legal Metrology", "Industrial Licensing",
];

const stats = [
  { value: "500+", label: "Businesses Served", icon: TrendingUp },
  { value: "14+", label: "Compliance Verticals", icon: ShieldCheck },
  { value: "100%", label: "On-time Filings", icon: Award },
  { value: "24 hrs", label: "Response Time", icon: Sparkles },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        path="/"
        title={PAGE_SEO["/"].title}
        description={PAGE_SEO["/"].description}
        jsonLd={[organizationJsonLd(), faqJsonLd(FAQS)]}
      />
      <Toaster richColors position="top-center" />
      {/* HERO */}
      <section className="relative min-h-[100vh] overflow-hidden pt-28">
        <div className="absolute inset-0 kb-glow-gold pointer-events-none" />
        <div className="absolute inset-0 kb-glow-navy pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[80vh]">
          <div className="lg:col-span-6 z-10">
            <Reveal>
              <div className="kb-label mb-6" data-testid="hero-eyebrow">
                Corporate Services · India
              </div>
            </Reveal>
            <Reveal delay={0.05}>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-black leading-[0.95] text-white" data-testid="hero-heading">
                Simplifying
                <br />
                <span className="text-[#d4a840] italic font-semibold">Compliance.</span>
                <br />
                Empowering Businesses.
              </h1>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-8 text-lg text-white/70 max-w-xl leading-relaxed">
                From incorporation to annual compliance — KaveriBiz is your single trusted partner
                for every regulatory, licensing and legal milestone in your business journey.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <div className="mt-10 flex flex-wrap gap-4" data-testid="hero-cta-group">
                <Link to="/contact" className="kb-btn-primary" data-testid="hero-cta-primary">
                  Book a Consultation <ArrowRight size={18} />
                </Link>
                <Link to="/services" className="kb-btn-ghost" data-testid="hero-cta-secondary">
                  Explore Services
                </Link>
              </div>
            </Reveal>

            <Reveal delay={0.35}>
              <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-xl">
                {stats.map((s) => (
                  <div key={s.label} className="border-l border-[#d4a840]/30 pl-4" data-testid={`stat-${s.label}`}>
                    <div className="font-display text-3xl font-bold text-white">{s.value}</div>
                    <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6 h-[420px] md:h-[520px] lg:h-[640px] relative" data-testid="hero-visual-wrap">
            <HeroShowcase />
            <div className="absolute -bottom-6 right-6 kb-glass rounded-2xl px-5 py-4 flex items-center gap-3">
              <ShieldCheck size={22} className="text-[#d4a840]" />
              <div className="leading-tight">
                <div className="text-xs text-white/60 uppercase tracking-widest">Trusted Partner</div>
                <div className="text-sm font-semibold text-white">In Every Business Journey</div>
              </div>
            </div>
          </div>
        </div>

        {/* Marquee */}
        <div className="relative border-y border-[#d4a840]/20 py-6 overflow-hidden bg-[#050A15]/60 backdrop-blur-sm">
          <div className="kb-marquee-track font-display text-2xl md:text-3xl text-white/40">
            {[...trustLine, ...trustLine].map((t, i) => (
              <div key={i} className="flex items-center gap-16">
                <span>{t}</span>
                <span className="text-[#d4a840]">✦</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="relative py-24 lg:py-32 max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
            <div>
              <div className="kb-label mb-4">What We Do</div>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-2xl">
                One firm. <span className="italic text-[#d4a840]">Every compliance</span> your business will ever need.
              </h2>
            </div>
            <Link to="/services" className="kb-btn-ghost" data-testid="services-view-all">
              View all services <ArrowRight size={16} />
            </Link>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.slice(0, 9).map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link
                  to={`/services/${s.slug}`}
                  data-testid={`service-card-${s.slug}`}
                  className="group block h-full kb-glass rounded-2xl p-7 transition-transform duration-300 hover:-translate-y-1"
                >
                  <div className="flex items-start justify-between mb-8">
                    <div className="h-12 w-12 rounded-xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center group-hover:bg-[#d4a840]/20 transition-colors">
                      <Icon size={22} className="text-[#d4a840]" />
                    </div>
                    <ArrowRight size={18} className="text-white/40 group-hover:text-[#d4a840] group-hover:translate-x-1 transition-all" />
                  </div>
                  <h3 className="font-display text-xl md:text-2xl font-semibold text-white leading-snug">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed">{s.tagline}</p>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="relative py-24 lg:py-32 border-y border-[#d4a840]/15 bg-gradient-to-b from-[#050A15] via-[#080f24] to-[#050A15]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="kb-label mb-4">Why KaveriBiz</div>
              <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight">
                A partner. <br />Not another <span className="italic text-[#d4a840]">vendor.</span>
              </h2>
              <p className="mt-6 text-white/70 leading-relaxed max-w-md">
                We combine the depth of a full-service law firm with the pace of a modern
                digital practice — so your compliance never becomes a bottleneck.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { title: "End-to-end coverage", body: "From company incorporation to annual compliance — 14+ verticals, one accountable partner." },
              { title: "CA-collaborative", body: "Financial, tax and GST due diligence delivered with chartered accountants for a 360° view." },
              { title: "Compliance calendars", body: "We track every deadline so you never miss a filing, licence renewal or statutory obligation." },
              { title: "Sector specialists", body: "Deep expertise in FSSAI, Legal Metrology, Fire & Environmental and Industrial licensing." },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 0.08}>
                <div className="kb-glass rounded-2xl p-6 h-full" data-testid={`why-card-${i}`}>
                  <div className="w-10 h-10 rounded-lg bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center mb-5">
                    <ShieldCheck size={18} className="text-[#d4a840]" />
                  </div>
                  <h3 className="font-display text-xl font-semibold text-white">{f.title}</h3>
                  <p className="text-white/65 text-sm mt-2 leading-relaxed">{f.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialsSection />

      {/* FAQ */}
      <FaqSection />

      {/* CTA */}
      <section className="relative py-24 lg:py-32">
        <div className="absolute inset-0 kb-glow-gold opacity-60 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <div className="kb-label mb-6">Ready when you are</div>
            <h2 className="font-display text-4xl md:text-6xl font-bold text-white leading-tight">
              Compliance Today. <br />
              <span className="italic text-[#d4a840]">Success Tomorrow.</span>
            </h2>
            <p className="mt-6 text-white/70 max-w-2xl mx-auto">
              Book a no-obligation consultation with our team and get a bespoke compliance
              roadmap for your business — in under 24 hours.
            </p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-10 flex flex-wrap justify-center gap-4"
            >
              <Link to="/contact" className="kb-btn-primary" data-testid="cta-book-now">
                Book Free Consultation <ArrowRight size={18} />
              </Link>
              <a href="tel:+918867192221" className="kb-btn-ghost" data-testid="cta-call">
                Call +91 88671 92221
              </a>
            </motion.div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <section className="relative py-24 lg:py-32 max-w-5xl mx-auto px-6 lg:px-10" data-testid="home-faq-section">
      <Reveal>
        <div className="kb-label mb-4">Common Questions</div>
        <h2 className="font-display text-4xl md:text-5xl font-bold text-white leading-tight max-w-2xl">
          Answers, <span className="italic text-[#d4a840]">before you even ask.</span>
        </h2>
      </Reveal>

      <div className="mt-12 space-y-3">
        {FAQS.map((f, i) => {
          const isOpen = i === openIndex;
          return (
            <Reveal key={f.q} delay={i * 0.04}>
              <div className={`rounded-2xl border transition-colors ${isOpen ? "border-[#d4a840]/40 bg-[#d4a840]/[0.04]" : "border-white/10 bg-white/[0.02]"}`}>
                <button
                  type="button"
                  onClick={() => setOpenIndex(isOpen ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  data-testid={`home-faq-${i}`}
                >
                  <span className="font-display text-lg text-white font-semibold">{f.q}</span>
                  <ChevronDown size={18} className={`text-[#d4a840] shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                <div
                  className="grid transition-[grid-template-rows] duration-300 ease-out"
                  style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
                >
                  <div className="overflow-hidden">
                    <p className="px-6 pb-5 text-sm text-white/70 leading-relaxed">{f.a}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}
